import { nanoid } from '@reduxjs/toolkit';
import { DimensionErrors } from '../../pages/Commodities/types';
import { MeasurementSystem } from '../../providers/User/User.model';
import { CommodityTableAttributes, CommodityList, CommodityUnit } from '../../redux/api/commodities/commodities.model';
import { Order, OrderCommodities } from '../../redux/api/orders/orders.model';
import { TripCommodities } from '../../redux/api/trips/trips.model';
import { UseCommoditySettingsResult } from '../../hooks/useCommoditySettings';

export const imperialToCm = (feet = 0, inches = 0): number => {
  return parseFloat(((feet * 12 + inches) * 2.54).toFixed(2));
};

export const metricToCm = (meters = 0, centimeters = 0): number => {
  return parseFloat((meters * 100 + centimeters).toFixed(2));
};

export const convertCmToFeetInches = (cm: string): { feet: string; inches: string } => {
  const totalInches = parseFloat(cm) / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = parseFloat((totalInches % 12).toFixed(2));
  return { feet: feet.toString(), inches: inches.toString() };
};

export const convertCmToMetersCentimeters = (
  cm: string
): { meters: number; centimeters: number } => {
  const meters = Math.floor(parseFloat(cm) / 100);
  const centimeters = parseFloat((parseFloat(cm) % 100).toFixed(2));
  return { meters, centimeters };
};

export const isValidNumber = (value: string): boolean => {
  return !isNaN(Number(value)) && value !== '';
};

export const formatDimensions = (
  length: number | null,
  width: number | null,
  height: number | null,
  measurementSystem: MeasurementSystem,
): string => {
  if (!length && !width && !height) return '';

  if (measurementSystem === 'IMPERIAL') {
    const lengthFt = length ? convertCmToFeetInches(length.toString()) : null;
    const widthFt = width ? convertCmToFeetInches(width.toString()) : null;
    const heightFt = height ? convertCmToFeetInches(height.toString()) : null;

    const formatValue = (
      value: { feet: string; inches: string } | null,
      type: 'L' | 'W' | 'H'
    ): string => {
      if (!value) return '';
      if (value.feet === '0' && value.inches === '0') return '';
      if (value.feet === '0') return `${value.inches}" ${type}`;
      if (value.inches === '0') return `${value.feet}' ${type}`;
      return `${value.feet}' ${value.inches}" ${type}`;
    };
    const formattedValues = [
      formatValue(lengthFt, 'L'),
      formatValue(widthFt, 'W'),
      formatValue(heightFt, 'H')
    ];

    return formattedValues.filter(Boolean).join(' x ');
  } else {
    const formattedValues = [
      length ? `${length.toFixed(2)} cm L` : '',
      width ? `${width.toFixed(2)} cm W` : '',
      height ? `${height.toFixed(2)} cm H` : ''
    ];
    return formattedValues.filter(Boolean).join(' x ');
  }
};

export const formatDimensionsV2 = (
  length: number | null,
  width: number | null,
  height: number | null,
  measurementSystem: MeasurementSystem
): string => {
  if (!length && !width && !height) return '';

  if (measurementSystem === 'IMPERIAL') {
    const lengthFt = length ? convertCmToFeetInches(length.toString()) : null;
    const widthFt = width ? convertCmToFeetInches(width.toString()) : null;
    const heightFt = height ? convertCmToFeetInches(height.toString()) : null;

    const formatValue = (
      value: { feet: string; inches: string } | null,
      type: 'L' | 'W' | 'H'
    ): string => {
      if (!value) return '';
      if (value.feet === '0' && value.inches === '0') return '';
      if (value.feet === '0') return `${type}: ${value.inches}"`;
      if (value.inches === '0') return `${type}: ${value.feet}'`;
      return `${type}: ${value.feet} ft' ${value.inches}"`;
    };

    const formattedValues = [
      formatValue(lengthFt, 'L'),
      formatValue(widthFt, 'W'),
      formatValue(heightFt, 'H')
    ];

    return formattedValues.filter(Boolean).join(', ');
  } else {
    const formattedValues = [
      length ? `L: ${length.toFixed(2)} cm` : '',
      width ? `W: ${width.toFixed(2)} cm` : '',
      height ? `H: ${height.toFixed(2)} cm` : ''
    ];
    return formattedValues.filter(Boolean).join(', ');
  }
};

export const validateDimensions = (
  length: string | null,
  width: string | null,
  height: string | null,
  measurementSystem: MeasurementSystem
): DimensionErrors => {
  const errors: DimensionErrors = {};

  if (!length && !width && !height) return errors;

  if (measurementSystem === 'IMPERIAL') {
    if (length && !isValidNumber(length)) {
      errors.length = 'Length must be a valid number';
    }

    if (width && !isValidNumber(width)) {
      errors.width = 'Width must be a valid number';
    }

    if (height && !isValidNumber(height)) {
      errors.height = 'Height must be a valid number';
    }
  } else {
    if (length && !isValidNumber(length)) {
      errors.length = 'Length must be a valid number';
    }

    if (width && !isValidNumber(width)) {
      errors.width = 'Width must be a valid number';
    }

    if (height && !isValidNumber(height)) {
      errors.height = 'Height must be a valid number';
    }
  }

  return errors;
};

export const unitOptions = [
  { value: CommodityUnit.PALLET, label: 'Pallet' },
  { value: CommodityUnit.BOX, label: 'Box' },
  { value: CommodityUnit.PIECE, label: 'Piece' },
  { value: CommodityUnit.CASE, label: 'Case' },
  { value: CommodityUnit.BUNDLE, label: 'Bundle' },
  { value: CommodityUnit.ROLL, label: 'Roll' },
  { value: CommodityUnit.DRUM, label: 'Drum' },
  { value: CommodityUnit.BAG, label: 'Bag' },
  { value: CommodityUnit.CARTON, label: 'Carton' },
  { value: CommodityUnit.SKID, label: 'Skid' },
  { value: CommodityUnit.GALLONS, label: 'Gallons' }
];

export const getUnitLabel = (unit: CommodityUnit | null | undefined): string => {
  return unit ? unitOptions.find((option) => option.value === unit)?.label || '' : '';
}

export const mapSavedOrderCommodityToCommodityList = (commodity: OrderCommodities): CommodityList => {
  const length = commodity.length
    ? convertCmToFeetInches(commodity.length.toString())
    : { feet: '', inches: '' };
  const width = commodity.width
    ? convertCmToFeetInches(commodity.width.toString())
    : { feet: '', inches: '' };
  const height = commodity.height
    ? convertCmToFeetInches(commodity.height.toString())
    : { feet: '', inches: '' };

  return {
    id: commodity.id,
    ui_uid: commodity.id ?? '',
    name: commodity.description ?? '',
    quantity: commodity.quantity,
    measurement_system: MeasurementSystem.IMPERIAL,
    length: commodity.length ? parseFloat(commodity.length.toFixed(2)) : null,
    width: commodity.width ? parseFloat(commodity.width.toFixed(2)) : null,
    height: commodity.height ? parseFloat(commodity.height.toFixed(2)) : null,
    permit: commodity.requires_permit ?? false,
    length_right: length.inches,
    length_left: length.feet,
    width_right: width.inches,
    width_left: width.feet,
    height_right: height.inches,
    height_left: height.feet,
    notes: commodity.notes ?? null,
    commodity_id: commodity.commodity_id ?? null,
    unit: commodity.unit ?? null,
    weight: commodity.weight,
    make: commodity.make ?? null,
    model: commodity.model ?? null,
    readable_commodity_id: commodity.readable_commodity_id ?? null,
  };
};

export const mapSavedTripCommodityToCommodityList = (commodity: TripCommodities, orderData?: Order | null): CommodityList => {
  const length = commodity.length
    ? convertCmToFeetInches(commodity.length.toString())
    : { feet: '', inches: '' };
  const width = commodity.width
    ? convertCmToFeetInches(commodity.width.toString())
    : { feet: '', inches: '' };
  const height = commodity.height
    ? convertCmToFeetInches(commodity.height.toString())
    : { feet: '', inches: '' };

  return {
    id: commodity.id,
    ui_uid: commodity.id ?? '',
    name: commodity.description ?? '',
    quantity: commodity.quantity || null,
    measurement_system: MeasurementSystem.IMPERIAL,
    length: commodity.length ? parseFloat(commodity.length.toFixed(2)) : null,
    width: commodity.width ? parseFloat(commodity.width.toFixed(2)) : null,
    height: commodity.height ? parseFloat(commodity.height.toFixed(2)) : null,
    permit: commodity.requires_permit ?? false,
    length_right: length.inches,
    length_left: length.feet,
    width_right: width.inches,
    width_left: width.feet,
    height_right: height.inches,
    height_left: height.feet,
    notes: commodity.notes ?? null,
    commodity_id: commodity.commodity_id ?? null,
    unit: commodity.unit ?? null,
    weight: commodity.weight || null,
    make: commodity.make ?? null,
    model: commodity.model ?? null,
    customer: orderData ? orderData.raw_customer?.name : commodity.customer,
    order_id: commodity.order_id,
    customer_number: orderData ? orderData.customer_order_number : commodity.customer_number,
    order_readable_id: orderData ? orderData.readable_id : commodity.order_readable_id,
    order_commodity_id: commodity.order_commodity_id,
    readable_commodity_id: commodity.readable_commodity_id ?? null,
  };
}

export const mapCommodityTableAttributesToCommodityList = (commodity: CommodityTableAttributes, commodityRowId: string, originalId?: string): CommodityList => {
  const length = commodity.length
    ? convertCmToFeetInches(commodity.length.toString())
    : { feet: '', inches: '' };
  const width = commodity.width
    ? convertCmToFeetInches(commodity.width.toString())
    : { feet: '', inches: '' };
  const height = commodity.height
    ? convertCmToFeetInches(commodity.height.toString())
    : { feet: '', inches: '' };

  return {
    id: originalId,
    ui_uid: commodityRowId,
    name: commodity.name,
    length_right: length.inches,
    length_left: length.feet,
    width_right: width.inches,
    width_left: width.feet,
    height_right: height.inches,
    height_left: height.feet,
    permit: commodity.requires_permit,
    notes: commodity.notes,
    unit: commodity.unit,
    weight: commodity.weight,
    commodity_id: commodity.id,
    readable_commodity_id: commodity.commodity_id,
    quantity: commodity.quantity,
    measurement_system: MeasurementSystem.IMPERIAL,
    length: commodity.length ? parseFloat(commodity.length.toFixed(2)) : null,
    width: commodity.width ? parseFloat(commodity.width.toFixed(2)) : null,
    height: commodity.height ? parseFloat(commodity.height.toFixed(2)) : null,
    make: commodity.make,
    model: commodity.model,
    customer: undefined,
    customer_number: undefined,
    order_id: undefined,
    order_readable_id: undefined
  };
};

export const mapPrefilledOrderCommodityToCommodityList = (commodity: TripCommodities): CommodityList => {
  const length = commodity.length
    ? convertCmToFeetInches(commodity.length.toString())
    : { feet: '', inches: '' };
  const width = commodity.width
    ? convertCmToFeetInches(commodity.width.toString())
    : { feet: '', inches: '' };
  const height = commodity.height
    ? convertCmToFeetInches(commodity.height.toString())
    : { feet: '', inches: '' };

  return {
    id: commodity.id,
    ui_uid: commodity.id ?? '',
    name: commodity.description ?? '',
    quantity: commodity.quantity,
    measurement_system: MeasurementSystem.IMPERIAL,
    length: commodity.length ? parseFloat(commodity.length.toFixed(2)) : null,
    width: commodity.width ? parseFloat(commodity.width.toFixed(2)) : null,
    height: commodity.height ? parseFloat(commodity.height.toFixed(2)) : null,
    permit: commodity.requires_permit ?? false,
    length_right: length.inches,
    length_left: length.feet,
    width_right: width.inches,
    width_left: width.feet,
    height_right: height.inches,
    height_left: height.feet,
    notes: commodity.notes ?? null,
    commodity_id: null,
    unit: commodity.unit ?? null,
    weight: commodity.weight,
    make: commodity.make ?? null,
    model: commodity.model ?? null,
    customer: commodity.customer,
    order_id: commodity.order_id,
    customer_number: commodity.customer_number,
    order_readable_id: commodity.order_readable_id,
    order_commodity_id: commodity.id,
    readable_commodity_id: commodity.readable_commodity_id ?? null,
  };
}

export const mapCommodityListToOrderCommoditiesForSave = (commodity: CommodityList, skipId?: boolean): OrderCommodities => {
  return {
    id: skipId ? undefined : commodity.id ?? undefined,
    ui_uid: commodity.ui_uid,
    description: commodity.name,
    quantity: commodity.quantity || 0,
    length: commodity.length ? parseFloat(commodity.length.toFixed(2)) : null,
    width: commodity.width ? parseFloat(commodity.width.toFixed(2)) : null,
    height: commodity.height ? parseFloat(commodity.height.toFixed(2)) : null,
    requires_permit: commodity.permit,
    notes: commodity.notes,
    unit: commodity.unit,
    packaging_unit: commodity.unit,
    weight: commodity.weight || 0,
    commodity_id: commodity.commodity_id ?? null,
    make: commodity.make ?? null,
    model: commodity.model ?? null,
    readable_commodity_id: commodity.readable_commodity_id ?? null,
  };
};

export const mapCommodityListToTripCommoditiesForSave = (commodity: CommodityList): TripCommodities => {
  return {
    ui_uid: commodity.ui_uid,
    description: commodity.name,
    quantity: commodity.quantity || 0,
    length: commodity.length ? parseFloat(commodity.length.toFixed(2)) : null,
    width: commodity.width ? parseFloat(commodity.width.toFixed(2)) : null,
    height: commodity.height ? parseFloat(commodity.height.toFixed(2)) : null,
    requires_permit: commodity.permit,
    notes: commodity.notes,
    unit: commodity.unit,
    packaging_unit: commodity.unit,
    weight: commodity.weight || 0,
    commodity_id: commodity.commodity_id ?? null,
    make: commodity.make ?? null,
    model: commodity.model ?? null,
    order_commodity_id: commodity.order_commodity_id,
    readable_commodity_id: commodity.readable_commodity_id ?? null,
  };
}

export const generateNewOrderCommodity = (measurementSystem: MeasurementSystem): CommodityList => {
  return {
    ui_uid: nanoid(),
    name: '',
    unit: null,
    weight: null,
    length: null,
    width: null,
    height: null,
    commodity_id: null,
    readable_commodity_id: null,
    notes: null,
    quantity: null,
    measurement_system: measurementSystem,
    length_right: null,
    length_left: null,
    width_right: null,
    width_left: null,
    height_right: null,
    height_left: null,
    permit: false,
    make: null,
    model: null
  }
}

export const generateNewTripCommodity = (measurementSystem: MeasurementSystem): CommodityList => {
  return {
    ui_uid: nanoid(),
    name: '',
    unit: null,
    weight: null,
    length: null,
    width: null,
    height: null,
    commodity_id: null,
    readable_commodity_id: null,
    notes: null,
    quantity: null,
    measurement_system: measurementSystem,
    length_right: null,
    length_left: null,
    width_right: null,
    width_left: null,
    height_right: null,
    height_left: null,
    permit: false,
    make: null,
    model: null,
    customer: '',
    customer_number: '',
    order_id: '',
    order_readable_id: ''
  }
}

export const getItemRows = ({ isDimensionsEnabled, isMakeEnabled, isModelEnabled, isUnitEnabled }: Pick<UseCommoditySettingsResult, 'isDimensionsEnabled' | 'isMakeEnabled' | 'isModelEnabled' | 'isUnitEnabled'>): number => {
  
  if (!isDimensionsEnabled && !isMakeEnabled && !isModelEnabled && isUnitEnabled) {
    return 6;
  }
  
  let rows = 0;
  if (isDimensionsEnabled) {
    rows += 3;
  }
  if (isMakeEnabled) {
    rows += 1;
  }
  if (isModelEnabled) {
    rows += 1;
  }
  if (isUnitEnabled) {
    rows += 1;
  }
  
  return 12 / rows;
}